import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Activity, AlertCircle } from "lucide-react";
import { RecordFilters } from "./RecordFilters";
import { PatientHealthRecordsList } from "./PatientHealthRecordsList";

interface HealthRecord {
  id: string;
  type: string;
  title: string;
  content?: string | null;
  text?: string | null;
  risk: string;
  ai_summary?: string | null;
  observed_at: string;
  metadata?: any;
  source_document?: any;
  source_type?: 'smartfile' | 'document' | null;
  source_session_id?: string | null;
  document_id?: string | null;
}

interface PatientHealthRecordsProps {
  patientId: string;
  records: HealthRecord[];
  onRefresh: () => void;
  onViewDocument?: (docId: string, docType: 'smartfile' | 'document') => void;
}

export function PatientHealthRecords({
  patientId,
  records,
  onRefresh,
  onViewDocument
}: PatientHealthRecordsProps) {
  const [filters, setFilters] = useState({
    type: 'all',
    risk: 'all',
    dateRange: 'all',
    hasDocument: 'all'
  });

  // Aplicar filtros
  const filteredRecords = records.filter(record => {
    if (filters.type !== 'all' && record.type !== filters.type) return false;
    if (filters.risk !== 'all' && record.risk !== filters.risk) return false;
    if (filters.hasDocument === 'with' && !record.source_document) return false;
    if (filters.hasDocument === 'without' && record.source_document) return false;

    if (filters.dateRange !== 'all') {
      const recordDate = new Date(record.observed_at);
      const today = new Date();
      const diffDays = Math.floor((today.getTime() - recordDate.getTime()) / (1000 * 60 * 60 * 24));

      if (filters.dateRange === '7days' && diffDays > 7) return false;
      if (filters.dateRange === '30days' && diffDays > 30) return false;
      if (filters.dateRange === '90days' && diffDays > 90) return false;
    }

    return true;
  });

  if (!records.length) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center space-y-3">
            <Activity className="h-12 w-12 mx-auto text-muted-foreground/50" />
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Nenhum registro de saúde encontrado
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Adicione registros clínicos ou capture documentos para começar
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Construir mapa de contagem de items
  const itemsCounts = filteredRecords.reduce((acc, record) => {
    acc[record.id] = (record as any).items_count || 0;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="space-y-4 w-full max-w-full overflow-x-hidden">
      <RecordFilters filters={filters} onFiltersChange={setFilters} />

      {filteredRecords.length === 0 ? (
        <Card>
          <CardContent className="py-8">
            <div className="text-center text-sm text-muted-foreground">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-50" />
              Nenhum registro encontrado com os filtros aplicados
            </div>
          </CardContent>
        </Card>
      ) : (
        <PatientHealthRecordsList
          records={filteredRecords}
          onViewDocument={onViewDocument}
          itemsCounts={itemsCounts}
        />
      )}

      {filteredRecords.length > 0 && (
        <div className="text-xs text-center text-muted-foreground pt-2">
          Exibindo {filteredRecords.length} de {records.length} registros
        </div>
      )}
    </div>
  );
}
